import * as React from 'react';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { ColumnsType } from 'antd/lib/table';
import { InfoCircleOutlined, WarningFilled } from '@ant-design/icons';
import { IosDependenciesConfig, IosDependenciesDependencyReport, IosDependenciesReport } from '@/types/iosDependencies';
import DependencyDetailContent from '@/components/dependencies/IosDependencyDetailPopover';
import FilesUsedPopover from '@/components/dependencies/FilesUsedPopover';
import { compareVersionStrings } from '@/components/dependencies/dependenciesUtils';
import {
    formatConstraint,
    formatMinIOS,
    formatStatusLabel,
    isIndirect,
    statusTagColor,
} from '@/components/dependencies/iosDependenciesUtils';
import { antGrey, rwColors } from '@/components/styles/globalStyles';

const makeDependencyColumns = (appMinIOSVersion?: string): ColumnsType<IosDependenciesDependencyReport> => [
    {
        title: 'Package name',
        dataIndex: 'identity',
        key: 'identity',
        width: 380,
        ellipsis: true,
        sorter: (a, b) => a.identity.localeCompare(b.identity),
        render: (identity: string, row) => (
            <Popover
                content={<DependencyDetailContent row={row} appMinIOSVersion={appMinIOSVersion} />}
                trigger="hover"
                placement="right"
                mouseEnterDelay={0.2}
            >
                <Space direction="vertical" size={0} style={{ cursor: 'help', maxWidth: '100%' }}>
                    <Text strong ellipsis>
                        {identity}
                        {isIndirect(row) && row.dependencyGraph && row.dependencyGraph[0] !== 'unknown' && (
                            <Text style={{ fontWeight: 'normal', color: antGrey.grey6 }}>
                                {` (${row.dependencyGraph.join(' → ')})`}
                            </Text>
                        )}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                        {row.location}
                    </Text>
                </Space>
            </Popover>
        ),
    },
    {
        title: 'Source',
        key: 'source',
        width: 110,
        sorter: (a, b) => {
            const aIndirect = isIndirect(a);
            const bIndirect = isIndirect(b);
            if (aIndirect === bIndirect) return 0;
            return aIndirect ? 1 : -1;
        },
        filters: [
            { text: 'Direct', value: 'direct' },
            { text: 'Indirect', value: 'indirect' },
        ],
        onFilter: (value, row) => (value === 'indirect' ? isIndirect(row) : !isIndirect(row)),
        render: (_, row) => (isIndirect(row) ? <Tag>Indirect</Tag> : <Tag color="magenta">Direct</Tag>),
    },
    {
        title: 'Dependency rule',
        key: 'pinnedAs',
        width: 180,
        sorter: (a, b) => {
            const aKind = a.versionConstraint?.kind ?? '';
            const bKind = b.versionConstraint?.kind ?? '';
            if (!aKind && !bKind) return 0;
            if (!aKind) return 1;
            if (!bKind) return -1;
            return aKind.localeCompare(bKind);
        },
        render: (_, row) => (row.versionConstraint ? <Text code>{formatConstraint(row.versionConstraint)}</Text> : '-'),
    },
    {
        title: 'Latest version requires',
        dataIndex: 'latestCompatibleMinIOS',
        key: 'latestCompatibleMinIOS',
        width: 130,
        sorter: (a, b, sortOrder) => {
            const aVal = (a.latestCompatibleMinIOS || a.latestMinIOS || '').trim();
            const bVal = (b.latestCompatibleMinIOS || b.latestMinIOS || '').trim();
            if (!aVal && !bVal) return 0;
            if (!aVal) return sortOrder === 'descend' ? -1 : 1;
            if (!bVal) return sortOrder === 'descend' ? 1 : -1;
            return compareVersionStrings(aVal, bVal);
        },
        render: (value: string, row) => {
            const shown = value || row.latestMinIOS;
            if (!shown) return '-';
            const shownExceeds = !value && row.latestMinIOSExceedsAppMin;
            if (!shownExceeds) return formatMinIOS(shown);
            const tooltip =
                appMinIOSVersion ?
                    `Requires iOS ${shown} but the app targets iOS ${appMinIOSVersion}`
                :   `Requires iOS ${shown}, which is higher than the app's minimum supported iOS version`;
            return (
                <Space size={4}>
                    <Text style={{ color: rwColors.statusYellow }}>{formatMinIOS(shown)}</Text>
                    <Tooltip title={tooltip}>
                        <WarningFilled style={{ color: rwColors.statusYellow }} />
                    </Tooltip>
                </Space>
            );
        },
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        sorter: (a, b) => a.status.localeCompare(b.status),
        filters: [
            { text: 'Up to date', value: 'up-to-date' },
            { text: 'Update available', value: 'update-available' },
            { text: 'Not checked', value: 'not-checked' },
            { text: 'No current version', value: 'no-current-version' },
        ],
        onFilter: (value, row) => row.status === value,
        render: (value: string, row) => (
            <Space direction="vertical" size={0}>
                <Tag color={statusTagColor(value)}>{formatStatusLabel(value)}</Tag>
                {row.checkError && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                        {row.checkError}
                    </Text>
                )}
            </Space>
        ),
    },
    {
        title: 'Current',
        dataIndex: 'currentVersion',
        key: 'currentVersion',
        width: 110,
        render: (value: string, row) => value || row.revision || '-',
    },
    {
        title: 'Latest compatible',
        dataIndex: 'latestCompatibleVersion',
        key: 'latestCompatibleVersion',
        width: 140,
        render: (value: string) => value || '-',
    },
    {
        title: 'Latest',
        dataIndex: 'latestVersion',
        key: 'latestVersion',
        width: 110,
        render: (value: string) => value || '-',
    },
];

const IosDependenciesPanelBody: React.FC<{
    destinationId: string;
    report: IosDependenciesReport;
    config?: IosDependenciesConfig;
    isLoading: boolean;
}> = ({ destinationId, report, config, isLoading }) => {
    const files: { label: string; path: string }[] = [];
    if (report.pbxprojPath) {
        files.push({ label: 'project.pbxproj', path: report.pbxprojPath });
    }
    if (report.packageResolvedPath) {
        files.push({ label: 'Package.resolved', path: report.packageResolvedPath });
    }
    for (const path of config?.xcconfigPaths ?? []) {
        if (path) files.push({ label: 'xcconfig', path });
    }

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {report.appMinIOSVersion && (
                <Space size={6}>
                    <Popover
                        content={<FilesUsedPopover files={files} />}
                        trigger="hover"
                        placement="right"
                        mouseEnterDelay={0.2}
                    >
                        <InfoCircleOutlined style={{ color: rwColors.primary, cursor: 'help' }} />
                    </Popover>
                    <Text type="secondary">Deployment target: {formatMinIOS(report.appMinIOSVersion)}</Text>
                </Space>
            )}
            <Table
                size="middle"
                rowKey={(row) => `${destinationId}-${row.identity}-${row.location}`}
                loading={isLoading}
                columns={makeDependencyColumns(report.appMinIOSVersion)}
                dataSource={report.dependencies}
                pagination={{
                    defaultPageSize: 25,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100', '200'],
                    showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
                }}
            />
        </Space>
    );
};

export default IosDependenciesPanelBody;
